import { create } from 'zustand';

import type { LocationId } from '@brightlocal/shared';

interface AiVisibilityExpectingRunStore {
  /**
   * Location ids for which a run was just accepted on the settings page.
   * Lets the report page show the generating screen immediately instead of the results skeleton
   * while the report / latest-run queries catch up.
   */
  isExpectingRunByLocationId: Partial<Record<LocationId, true>>;
  /** Records that a run was just started for this location (warm path after Start tracking). */
  setIsExpectingRun: (locationId: LocationId) => void;
  /** Drops the warm-path intent once the report page has real latest-run data. */
  clearIsExpectingRun: (locationId: LocationId) => void;
}

/**
 * Ephemeral cross-route UI intent for AI Visibility: set on successful submit, consumed on the
 * report page, cleared when the API-driven run state is known. Not persisted — cold reload learns
 * from the API (brief skeleton is acceptable).
 */
export const useAiVisibilityExpectingRunStore = create<AiVisibilityExpectingRunStore>((set) => ({
  isExpectingRunByLocationId: {},
  setIsExpectingRun: (locationId) =>
    set((state) => ({
      isExpectingRunByLocationId: { ...state.isExpectingRunByLocationId, [locationId]: true }
    })),
  clearIsExpectingRun: (locationId) =>
    set((state) => {
      if (state.isExpectingRunByLocationId[locationId] !== true) {
        return state;
      }

      const isExpectingRunByLocationId = { ...state.isExpectingRunByLocationId };

      delete isExpectingRunByLocationId[locationId];

      return { isExpectingRunByLocationId };
    })
}));
